import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ConfirmProvider } from './components/Confirm';
import { router } from './router';
import './styles.css';
import { applyStoredThemeEarly } from './lib/theme';
import { applyStoredAppIconEarly } from './lib/app-icon';
import { registerServiceWorker } from './data/push';
import { initErrorReporting } from './lib/error-report';
import { initVitals } from './lib/vitals';
import { trackVisualViewport } from './lib/viewport';
import { captureInstallPrompt } from './lib/install';
import { installEdgeSwipeBack } from './lib/edge-swipe';

// Meg az elso festes elott, hogy ne villanjon vilagos hatter sotet temanal.
applyStoredThemeEarly();

// Ugyanezert: a fulon ne a petrol ikon villanjon fel a valasztott helyett.
applyStoredAppIconEarly();

// Telefonon a lathato terulet (billentyuzet!) kovetese - lasd lib/viewport.ts.
trackVisualViewport();
captureInstallPrompt();
installEdgeSwipeBack(() => {
  if (router.history.canGoBack()) router.history.back();
});

/*
  A HIVAS OLDALA elore letoltodik, amikor a bongeszo raer (K2).

  Kulon darabban van (router.tsx), es terero nelkul nem lehetne letolteni -
  a hivas gomb ilyenkor hibaoldalra vinne a "nincs kapcsolat" magyarazat
  helyett. Igy a service worker mar a gyorsitotarban tartja.
*/
const preloadCallPage = () => void import('./routes/huddle-window').catch(() => undefined);
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(preloadCallPage, { timeout: 10_000 });
} else {
  setTimeout(preloadCallPage, 5_000);
}

/**
 * A service worker CSAK az ertesiteshez kell (lasd public/sw.js).
 * Nem varunk ra: ha nem sikerul, az alkalmazas ugyanugy mukodik.
 */
void registerServiceWorker();

/**
 * A hibajelentes MINDEN mas elott indul.
 *
 * Amit a legjobban latni akarunk, az a betoltes elso masodperceiben tortent
 * hiba - olyankor a felhasznalo csak annyit lat, hogy "nem indul el".
 */
void initErrorReporting();
// A felulet sebessege a kollega gepen - oldalbetoltesenkent egy minta.
initVitals();

/**
 * VISSZATERESKOR FRISSITUNK.
 *
 * Eddig `false` volt, es ezzel egyetlen mondatban le lehetett irni a hibat:
 * ami nem kap valos ideju esemenyt, az SOHA nem frissult ujra. Egy bongeszo-
 * fulon ez elbujt, mert elobb-utobb mindenki nyomott egy F5-ot - a telepitett
 * alkalmazasban viszont nincs is mivel: ott az adat addig allt, amig valaki
 * ujra nem inditotta az egeszet.
 *
 * A TanStack Query a lap LATHATOSAGARA figyel, nem az ablak fokuszara, tehat
 * pont az az eset valtja ki, amirol szo van: elokerul az alkalmazas, es amit
 * latunk, az friss. A `staleTime` fogja vissza - a legtobb lekeresnel 30-60
 * masodperc -, ezert az ide-oda kattintas nem jelent uj halozati kort.
 *
 * Amit a WebSocket tart naprakeszen (pl. az uzenetfolyam), az `staleTime:
 * Infinity`-vel vedi magat ettol.
 */
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: true } },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root elem hiányzik az index.html-ből');

/**
 * A MEGSZAKADT view transition nem hiba.
 *
 * Ha egy atmenet (pl. uj uzenet miatti oldalsav-atrendezes) hatterben levo
 * lapon indul, a bongeszo "Document hidden" InvalidStateError-ral megszakitja -
 * a felulet ettol meg helyesen frissul, csak animacio nelkul. A React ezt
 * alapbol `reportError`-ral jelenti, az pedig a globalis `error` esemenyen at
 * a hibakovetobe menne: minden hatterben erkezo uzenet egy hamis hibajegy lenne.
 */
function onRecoverableError(error: unknown): void {
  if (error instanceof DOMException && ['InvalidStateError', 'AbortError'].includes(error.name))
    return;
  reportError(error);
}

createRoot(container, { onRecoverableError }).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/*
        A megerosito parbeszed a LEGKULSO reteg: barhonnan hivhato, es a
        felette levo parbeszedeket is at kell fednie (pl. a csatorna tagjai
        ablakbol indulo kivetel).
      */}
      <ConfirmProvider>
        <RouterProvider router={router} />
      </ConfirmProvider>
    </QueryClientProvider>
  </StrictMode>,
);
